import { yupResolver } from '@hookform/resolvers/yup';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { RadioCard, RadioGroup } from '@zorro/design-system/v2/Radio';
import { FormErrorMessage, Group, Stack } from '@zorro/web/design-system';
import {
  screamingSnakeCaseToText,
  useBatchCallEndpoint,
  useForm,
  useIdempotencyKey,
} from '@zorro/web/ui-utils';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { FormFooter } from '../../FormFooter';
import { InformationBoxComponent } from '../../InformationBox';
import { PlanMarketConfig } from '../../PlanMarketBadgePageView';
import { RecordForActions } from '../EmployeeActionsUtils';
import { ErrorsTable } from '../Errors/ErrorsTable';
import {
  ConfirmEnrollmentFormFields,
  confirmEnrollmentSchema,
  planMarketEnrollmentValues,
} from './confirmEnrollmentForm.utils';

type ConfirmEnrollmentFormProps = {
  recordsForActions: RecordForActions[];
  onClose: () => void;
  onSuccess: () => void;
};

export const ConfirmEnrollmentForm = ({
  recordsForActions,
  onClose,
  onSuccess,
}: ConfirmEnrollmentFormProps) => {
  const idempotencyKey = useIdempotencyKey();

  const {
    control,
    formState: { errors, isValid },
    getValues,
  } = useForm<ConfirmEnrollmentFormFields>({
    mode: 'all',
    resolver: yupResolver(confirmEnrollmentSchema),
  });

  const { executeBatchCall, errors: bulkErrors } = useBatchCallEndpoint({
    methodName: 'majorMedicalControllerConfirmEnrollment',
    singularItemName: 'employee',
    action: 'updated',
    batchSize: 5,
  });

  async function handleSubmit() {
    const { planMarket: planMarketFormValue } = getValues();
    const planMarket =
      planMarketFormValue === 'NOT_APPLICABLE' ? null : planMarketFormValue;
    const batchItems = recordsForActions.map(({ fullName, benefitId }) => ({
      key: fullName,
      params: [
        benefitId as string, // modal is only available for submitted employees
        idempotencyKey.getFor(benefitId as string),
        { planMarket },
      ] as const,
    }));

    await executeBatchCall(batchItems, {
      onSuccess,
    });
  }

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Confirm enrollment"
        primaryButtonProps={{ onClick: handleSubmit, disabled: !isValid }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{
          onClick: onClose,
        }}
      />
    ),
    [handleSubmit, isValid, onClose]
  );
  useDrawerFooter(drawerFooter);

  return (
    <Stack>
      <Stack>
        <InformationBoxComponent
          title="Please note"
          items={[
            "It's recommended to confirm enrollments via individual employee pages where all details can be verified",
            "When confirming in bulk, enrollment fields will remain at their default values. It's your responsibility to confirm/update the coverage period, collect enrollment evidence, update member ID, and track payment status on individual pages",
            "If there are any changes between an employee's election and the final confirmation (e.g. premium, tax advantage, or coverage dates), ensure the employee is informed",
          ]}
        />
        <Controller
          name="planMarket"
          control={control}
          render={({ field: { value, ...rest } }) => (
            <RadioGroup
              {...rest}
              value={value}
              label="Enrollment type"
              required
            >
              <Group wrap="nowrap" gap="sm" grow>
                {planMarketEnrollmentValues.map((radioValue) => {
                  const { color, icon: IconComponent } =
                    PlanMarketConfig[radioValue];
                  const label = screamingSnakeCaseToText(radioValue);

                  return (
                    <RadioCard
                      icon={<IconComponent />}
                      iconColor={color}
                      key={radioValue}
                      value={radioValue}
                      checked={radioValue === value}
                      label={label}
                    />
                  );
                })}
              </Group>
            </RadioGroup>
          )}
        />
        <FormErrorMessage fieldName="planMarket" errors={errors} />
      </Stack>

      <ErrorsTable
        tableTitle="Employee name"
        errors={bulkErrors}
        isBulk={recordsForActions.length > 1}
      />
    </Stack>
  );
};
